import React, { useCallback, useMemo, useState } from "react";
import {
    HistoryField,
    HistoryGroupBy,
    SCHEDULE_RESOURCE_TYPES,
} from "@common/constants/history-source-types";
import {
    APIProcessName,
    SQSProcessName,
} from "@common/constants/process-names";
import { ActivityOutcomeKind } from "@common/schemas/domains/activity/activity-outcomes.schema";
import { AuditChannel, ResourceType } from "@common/types/auditLog";
import { RevertedFilterValue } from "@hooks/view/useHistoryPageFiltersCache";
import { ChildrenProps } from "../../types";
import { HistoryRow } from "./views/historyViewConfig";
import {
    historyDrawerContext,
    HistoryDrawerContextValue,
} from "./historyDrawerContext";
import { HistoryDetailsDrawer } from "./details/HistoryDetailsDrawer";
import { GroupedHistoryDrawer } from "./details/reconciliation/GroupedHistoryDrawer";
import { SplitHistoryDrawer } from "./details/split/SplitHistoryDrawer";
import { JournalEntryHistoryDrawer } from "./details/journalEntries/JournalEntryHistoryDrawer";
import { BatchHistoryDrawer } from "./details/batch/BatchHistoryDrawer";
import { CoaTemplateActivityDrawer } from "./details/coaTemplate/CoaTemplateActivityDrawer";
import { isCoaTemplateApplyEventRow } from "./details/coaTemplateApply/coaTemplateApplyHistoryDrawer.utils";
import { ScheduleActivityHistoryDrawer } from "./details/schedules/ScheduleActivityHistoryDrawer";
import { ReportSettingsActivityHistoryDrawer } from "./details/reportSettings/ReportSettingsActivityHistoryDrawer";

interface Props extends ChildrenProps {
    revertedFilter?: RevertedFilterValue;
    channels?: AuditChannel[];
    outcome?: ActivityOutcomeKind;
    isGroupedByChannel?: boolean;
}

export const HistoryDrawerContextProvider: React.FC<Props> = ({
    children,
    revertedFilter,
    channels,
    outcome,
    isGroupedByChannel,
}) => {
    const [open, setOpen] = useState(false);
    const [historyRow, setHistoryRow] = useState<HistoryRow | null>(null);

    const openDrawer = useCallback((row: HistoryRow) => {
        setHistoryRow(row);
        setOpen(true);
    }, []);

    const closeDrawer = useCallback(() => {
        setOpen(false);
    }, []);

    const contextValue = useMemo<HistoryDrawerContextValue>(
        () => ({
            open,
            historyRow,
            revertedFilter,
            channels,
            outcome,
            isGroupedByChannel,
            openDrawer,
            closeDrawer,
        }),
        [
            open,
            historyRow,
            revertedFilter,
            channels,
            outcome,
            isGroupedByChannel,
            openDrawer,
            closeDrawer,
        ],
    );

    const isCoaApplyEvent = isCoaTemplateApplyEventRow(historyRow);

    const drawer = useMemo(() => {
        const isJournalEntry =
            historyRow?.resourceType === ResourceType.JOURNAL_ENTRY ||
            historyRow?.resourceType === ResourceType.MANUAL_JOURNAL_ENTRY;
        const groupBy = historyRow?.historyItem.groupBy;
        const resourceType = historyRow?.resourceType;
        const sourceType = historyRow?.sourceType;
        const fields = historyRow?.fields;

        // Route the CoA template apply event to its dedicated historical drawer
        // before any generic handling (the apply event IS the resource).
        if (isCoaApplyEvent) {
            return <CoaTemplateActivityDrawer />;
        }

        if (resourceType && SCHEDULE_RESOURCE_TYPES.includes(resourceType)) {
            return <ScheduleActivityHistoryDrawer />;
        }

        if (resourceType === ResourceType.REPORT_SETTINGS) {
            return <ReportSettingsActivityHistoryDrawer key={historyRow?.id} />;
        }

        const isBatchProcess =
            sourceType === APIProcessName.TRANSACTION_BULK_UPDATE ||
            sourceType === SQSProcessName.TRANSACTION_RULE_APPLICATION;

        if (isBatchProcess) {
            return <BatchHistoryDrawer />;
        }

        const isSplitFieldUpdate =
            groupBy === HistoryGroupBy.FIELD &&
            resourceType === ResourceType.TRANSACTION &&
            fields?.length === 1 &&
            fields[0] === HistoryField.SPLIT_PARENT_ID;

        if (
            groupBy === HistoryGroupBy.SOURCE ||
            groupBy === HistoryGroupBy.DIFF_VALUE
        ) {
            return <GroupedHistoryDrawer />;
        }
        if (
            groupBy === HistoryGroupBy.SOURCE_COUNT_DISTINCT ||
            isSplitFieldUpdate
        ) {
            if (resourceType === ResourceType.TRANSACTION) {
                return <SplitHistoryDrawer />;
            }
            if (isJournalEntry) {
                return <JournalEntryHistoryDrawer />;
            }
            return <HistoryDetailsDrawer />;
        }
        return <HistoryDetailsDrawer />;
    }, [
        historyRow?.historyItem.groupBy,
        historyRow?.id,
        historyRow?.resourceType,
        historyRow?.sourceType,
        historyRow?.fields,
        isCoaApplyEvent,
    ]);

    return (
        <historyDrawerContext.Provider value={contextValue}>
            {children}
            {drawer}
        </historyDrawerContext.Provider>
    );
};
